import React from 'react';
import classnames from 'classnames';

import { Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';
import { useActiveSection } from '@hydra/common/components/ScrollSpy';
import { useReviewContext } from '@hydra/common/context';

import mapModuleToIcon from '@hydra/utils/mapModuleToIcon';
import { sendEvent } from '@hydra/utils/gtm';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'sticky',
    top: '80px',
    minWidth: 240,
    marginTop: theme.spacing(2.5),
    listStyleType: 'none',
    paddingLeft: 0,
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  hide: { display: 'none' },
  show: { display: 'block' },
  text: {
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold,
    maxWidth: 130,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
    whiteSpace: 'nowrap'
  },
  icon: {
    marginRight: theme.spacing(1),
    verticalAlign: 'sub'
  },
  section: {
    display: 'block',
    color: theme.palette.grey[400],
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold,
    padding: theme.spacing(0.5, 3),
    marginTop: theme.spacing(1),
    textTransform: 'uppercase'
  },
  moduleItem: {
    display: 'block',
    color: theme.palette.text.primary,
    padding: theme.spacing(0, 3)
  },
  dashboard: {
    display: 'block',
    color: theme.palette.text.primary,
    padding: theme.spacing(0.5, 3)
  },
  active: {
    color: theme.palette.primary.main
  },
  noUnderline: {
    '&:hover, &:focus, &:active': {
      textDecoration: 'none'
    }
  },
  list: {
    listStyle: 'none',
    padding: 0
  },
  topZero: {
    top: 0
  }
}));

const SectionItem = ({ section }) => {
  const { isShopify } = useReviewContext();
  const classes = useStyles();
  return (
    <Link
      href={`#module-section-title-${section}`}
      className={classnames(
        classes.section,
        classes.noUnderline
      )}
      onClick={(e) => {
        sendEvent('review-anchornav-category-link', { category: section });
        if (isShopify) {
          e.preventDefault();
          document.getElementById(`module-section-title-${section}`).scrollIntoView();
        }
      }}
    >
      <Translate content={`wip_section_${section}`} />
    </Link>
  );
};

const ModuleItem = ({ module }) => {
  const classes = useStyles();
  const activeSection = useActiveSection();
  const { isShopify } = useReviewContext();
  return (
    <Link
      key={module}
      href={`#module-${module}`}
      className={classnames(
        classes.moduleItem,
        classes.noUnderline,
        activeSection === module && classes.active
      )}
      onClick={(e) => {
        sendEvent('review-anchornav-category-link', { category: module });
        if (isShopify) {
          e.preventDefault();
          document.getElementById(`module-${module}`).scrollIntoView();
        }
      }}
    >
      <span className={classes.icon}>
        <Icon type={mapModuleToIcon(module)} size='sm' />
      </span>
      <Translate content={module} className={classes.text} />
    </Link>
  );
};

const ReviewNav = ({ showModule, showSection, reviewStructure, headerHidden = false }) => {
  const classes = useStyles();
  const activeSection = useActiveSection();
  const showSections = {
    on_page: showSection({ section: 'on_page' }),
    off_page: showSection({ section: 'off_page' })
  };
  const { isShopify } = useReviewContext();

  return (
    <div
      className={classnames(
        classes.root,
        headerHidden && classes.topZero,
        !showSections.on_page && !showSections.off_page && classes.hide
      )}
    >
      <li>
        <Link
          href='#dashboard'
          onClick={(e) => {
            sendEvent('review-anchornav-returntotop-link');
            if (isShopify) {
              e.preventDefault();
              document.getElementById('dashboard').scrollIntoView();
            }
          }}
          className={classnames(
            classes.dashboard,
            classes.noUnderline,
            activeSection === 'dashboard' && classes.active
          )}
        >
          <span className={classes.icon}>
            <Icon type='chevron-up' size='sm' />
          </span>
          <Translate content='return_to_top' className={classes.text} />
        </Link>
      </li>
      <li>
        {Object.keys(reviewStructure).map(section => (
          <ul
            key={section}
            className={classnames(
              showSections[section] ? classes.show : classes.hide,
              classes.list
            )}
          >
            <li>
              <SectionItem section={section} />
            </li>
            {reviewStructure[section] && Object.keys(reviewStructure[section]).map(module => (
              <li key={module} className={showModule({ module, section }) ? classes.show : classes.hide}>
                <ModuleItem module={module} />
              </li>
            ))}
          </ul>
        ))}
      </li>
    </div>
  );
};

export default React.memo(ReviewNav);
